import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Board {
  name: string;
  body: string;
}

const BOARDS: Board[] = [
  {
    name: 'Beginner',
    body: 'Nine by nine with ten mines — one square in eight. Small enough to keep the whole board in your head, and forgiving enough that patient counting will nearly always get you out. Two or three minutes a game.',
  },
  {
    name: 'Intermediate',
    body: 'Sixteen by sixteen with forty. The density barely moves, but the board is four times the size, so you end up working three or four separate regions at once and the openings stop joining up into one.',
  },
  {
    name: 'Expert',
    body: 'Thirty by sixteen with ninety-nine — one square in five. At that density the board regularly offers you nothing certain at all, and finishing means picking the better of two guesses and living with the result.',
  },
];

export function MinesweeperHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Open every square that is not a mine. Every number you uncover tells you exactly how many
          mines are touching it — and that is all the information there is, which turns out to be
          nearly enough.
        </p>
      </header>

      <section className="prose__section">
        <h2>Opening squares</h2>
        <p>
          Click any square to open it. Your <strong>first click is always safe</strong>, and by
          default it is more than safe: the mines are laid around it after you have chosen, so it
          always comes up blank and always opens a whole region. That is not the site being kind — a
          first move that can kill you is a coin toss, and a coin toss is not the game.
        </p>
        <p>
          A square with no mines around it is blank, and blanks open everything they touch, which
          keeps going until the wave reaches numbers. That is why one click can clear a quarter of
          the board. A square with mines around it shows a number from <strong>1</strong> to{' '}
          <strong>8</strong>: how many of its eight neighbours are mined, diagonals included.
        </p>
      </section>

      <section className="prose__section">
        <h2>Flags</h2>
        <p>
          <strong>Right-click</strong> a square to flag it, or turn on <strong>flag mode</strong>{' '}
          under the board and press it normally — which is the way in on a phone, and from a
          keyboard. A flagged square cannot be opened by accident, which is the entire point of it.
        </p>
        <p>
          Flags are for you, not for the game. You do not have to place a single one to win, and
          placing one does not tell the board anything it did not know. What they are for is
          arithmetic: once a mine is marked, the numbers around it can be read as "and how many
          besides that one".
        </p>
      </section>

      <section className="prose__section">
        <h2>Chording — the move worth learning</h2>
        <p>
          Press a number that already has that many flags around it, and everything else touching it
          opens at once. A <strong>3</strong> with three flags around it has nothing left to hide, so
          its remaining neighbours are all safe and there is no reason to click them one at a time.
        </p>
        <p>
          It is the difference between playing Minesweeper slowly and playing it properly — and it is
          the one move in the game that takes you at your word. If one of those flags is wrong, the
          board opens a mine on your instruction and that is the end of it. No warning, because you
          told it you were sure.
        </p>
      </section>

      <section className="prose__section">
        <h2>Reading two numbers together</h2>
        <p>
          One number on its own rarely settles anything. Two overlapping ones usually do, and this is
          the whole skill. If a <strong>1</strong> and a <strong>2</strong> sit side by side and share
          two closed squares, and the <strong>1</strong> also touches a third square the{' '}
          <strong>2</strong> does not, then the <strong>2</strong>'s extra mine has nowhere to be
          except that third square — which the <strong>1</strong> now accounts for entirely, making
          everything else it touches safe.
        </p>
        <p>
          Almost every board can be taken apart that way. When people say Minesweeper is a guessing
          game, they mean they stopped at the first number.
        </p>
      </section>

      <section className="prose__section">
        <h2>Three boards</h2>
        <ul className="prose__list">
          {BOARDS.map((board) => (
            <li key={board.name}>
              <div>
                <h3>{board.name}</h3>
                <p>{board.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <p>
          Any size between five and thirty across is available, with as many mines as will fit, and
          both house rules can be turned off: the guaranteed opening, and the flag limit. Turning off
          the flag limit lets the counter go below zero, which is useful when you want to mark two
          squares as "one of these is a mine" without yet deciding which.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
